/**
 * Login Page Component
 *
 * Displayed when user is not authenticated.
 * Provides Microsoft SSO login button.
 */

import React, { useState } from 'react';
import { useAuth } from './AuthProvider';

export function LoginPage(): React.ReactElement {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await login();
    } catch (err) {
      console.error('Login error:', err);
      setError('Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-charcoal-900 via-charcoal-800 to-charcoal-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white flex items-center justify-center shadow-lg">
            <span className="text-5xl">🍒</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">CherryPulse</h1>
          <p className="text-charcoal-400">Agentur-Qualität messbar machen</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            Anmelden
          </h2>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Microsoft Login Button */}
          <button
            onClick={() => void handleLogin()}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white hover:bg-charcoal-50 text-charcoal-900 font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-charcoal-300 border-t-charcoal-600 rounded-full animate-spin" />
                <span>Anmelden...</span>
              </>
            ) : (
              <>
                {/* Microsoft Logo */}
                <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                <span>Mit Microsoft anmelden</span>
              </>
            )}
          </button>

          <p className="mt-6 text-center text-charcoal-500 text-sm">
            Melden Sie sich mit Ihrem Unternehmenskonto an
          </p>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-charcoal-600 text-xs">
          &copy; {new Date().getFullYear()} CherryPicker. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
}
